import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            About Us
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Local professionals who help people make sense of technology
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            Who We Are
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p>
              We're a small consulting practice based in West Texas. We work with local businesses, nonprofits, and public organizations that want to understand their technology and make good decisions.
            </p>
            <p>
              We're not an IT company. We don't sell software or manage networks. We help when something feels unclear, uncertain, or overly complicated.
            </p>
            <p>
              Most organizations don't need more technology — they need clarity. Our job is to explain what matters, what doesn't, and what to do next.
            </p>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-12 text-center">
            What We Believe
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Technology Should Make Sense
              </h3>
              <p className="text-gray-700 leading-relaxed">
                If it can't be explained in regular language, it probably isn't being understood correctly.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Security Is About People
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Most problems happen because of confusion, not hackers. When people understand what to look for, risk drops fast.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Honest Advice Matters
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We don't resell products or chase commissions. That makes it easier to give straight answers.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                Local Relationships Work
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Face-to-face conversations build trust. Being nearby matters, especially when things feel uncertain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            Our Experience
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              Our background comes from working inside a large Texas public institution rather than outside consulting.
            </p>
            <p>
              Lance began on the help desk working nights and weekends with no formal IT training and learned the field through hands-on experience. Over time he moved into project work, then project management, and eventually led statewide IT project management and vendor management efforts. His work focused on evaluating software, guiding purchasing decisions, and helping departments choose tools that actually fit their needs. Lance now runs Lone Star Republic Consulting and continues that same practical approach in his work with organizations.
            </p>
            <p>
              Scott worked as a cybersecurity engineer responsible for protecting systems and responding to real threats. His responsibilities included security operations, risk reduction, and TAC 202 compliance. Before moving into cybersecurity, he worked directly with systems in the field — installing, troubleshooting, and supporting hardware and network infrastructure, and later overseeing that work. That background shapes his approach to security, focusing on how systems actually operate day to day. Before entering IT, he spent years managing restaurants, which shapes how he approaches practical, day-to-day operational problems.
            </p>
            <p>
              Together, our experience comes from working inside an environment where decisions affected real staff, real students, and daily operations. We've seen what happens when technology is chosen well — and when it isn't. Both of us were involved in software rollouts and implementations, working directly with the people expected to use the systems. That experience shaped how we approach change — focusing on clarity, timing, and minimizing disruption.
            </p>
            <p>
              What we bring isn't theory or vendor messaging. It's perspective gained from being responsible for systems people relied on every day.
            </p>
          </div>
        </div>
      </section>

      {/* Why We Do This */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8">
            Why We Do This Work
          </h2>
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-lg">
              Too many organizations feel pressured into decisions they don't fully understand.
            </p>
            <p className="text-lg">
              We started this practice because we kept meeting people who just wanted a clear answer from someone without an agenda.
            </p>
            <p className="text-lg">
              Someone who would explain the situation plainly, then let them decide.
            </p>
            <p className="text-lg font-medium text-[#1e3a5f]">
              That's the role we try to fill.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Wondering if we're a good fit?
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us what you're trying to figure out. We'll tell you honestly whether we can help.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Get in touch
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
